import { Section, SectionHeader } from '../../../../../styling/FormStyling';
import TextRow, {FlexRow, RowBase, RowLabel, RowValue} from '../../components/TextRow';
import { StyledSectionContent, TaskDetailsContext, TaskDetailsDispatchContext } from '../TaskDetailsStep';
import { ColumnFlex2, ColumnFlex3 } from '../../../../../styling/Styling';
import AddressAutocomplete from '../../../../../components/address-autocomplete/AddressAutocomplete';
import { WorkTaskDTOExtended } from '../../../../../models/TaskType';
import { getLocationString } from '../../../../../utils/location/locationHandling';
import { getEnumDisplayValue } from '../../../../../utils/enumUtils';
import { AssetType, LocationDTO, UserDTO, WorkTaskStatus } from '../../../../../api/api';
import styled from 'styled-components';
import MapComponent from '../../../../../components/map-component/MapComponent';
import BasicPopover from '../../../../../components/popover/Popover';
import Person from '../../../../../components/person/Person';
import ContactCard from '../../../../../components/contact-card/ContactCard';
import Button from '../../../../../components/button/Button';
import { WorldIcon } from '../../../../../assets/icons/WorldIcon';
import { useContext, useEffect, useState } from 'react';
import AutoComplete from '../../../../../components/auto-complete/AutoComplete';
import TextField from '../../../../../components/text-field/TextField';
import UserService from '../../../../../services/UserService';
import NotificationService from '../../../../../services/NotificationService';
import { MeterTaskDetailsContext, MeterTaskDetailsDispatchContext } from '../MeterTaskDetailsStep';
import {useConfirmationDialog} from "../../../../../hooks/useConfirmationDialog";
import TaskService from "../../../../../services/TaskService";
import {getUserEmail} from "../../../../../utils/authProvider/authProvider";
import {
  setWorkPlanShouldUpdate
} from "../../../../../stateManagement/reducers/workPlanViewReducer";
import {store} from "../../../../../stateManagement/store";
import { userAllowedThroughFeatureFlag } from '../../../../../utils/validation/userAllowedFeatureFlag';
import { openFieldMaps } from '../../../../../utils/fieldMaps';

interface Props {
  editable?: boolean;
  task?: WorkTaskDTOExtended;
  toggleFieldIsDirty: (key: string, value: boolean) => void;
  onTaskUpdated?: (task: WorkTaskDTOExtended) => void;
}

interface Technician {
    name?: string;
    email?: string;
}

export const Information = (props: Props) => {
  const { editable, task, toggleFieldIsDirty, onTaskUpdated } = props;
  const dispatchTaskDetails = useContext(task?.isMeterTask ? MeterTaskDetailsDispatchContext : TaskDetailsDispatchContext);
  const taskDetailsState = useContext(task?.isMeterTask ? MeterTaskDetailsContext : TaskDetailsContext);
  const { taskLocation } = taskDetailsState;
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [isAssignToMeAllowed, setIsAssignToMeAllowed] = useState<boolean>(false);
  const hasBookings = task?.bookings
  const {getConfirmation} = useConfirmationDialog()
  
  useEffect(() => {
    const myEmail = getUserEmail()
    
    const isAllowed = myEmail !== task?.assignedToEmail && task?.status === WorkTaskStatus.Ongoing;
    setIsAssignToMeAllowed(isAllowed);
  },[task?.id, task?.assignedToEmail]);
  
  const ensureAssignToMe = async () => {
    const result = await getConfirmation({
      headerText: 'Tildel første mand til mig?',
      bodyText: 'Du er ved at overtage ansvaret som første mand. Vil du fortsætte?',
      confirmButtonText: "Ja, tildel til mig",
      declineButtonText: "Annuller",
    }, false);
    
    console.log("Result from confirm dialog:", result);
    if (result === "confirm" && task?.id) {
      let taskId = parseInt(task.id);
      await TaskService.assignToMe(taskId);
      const refreshedTask = await TaskService.getTask(taskId);
      dispatchTaskDetails({type: 'set-task', payload: refreshedTask});
      onTaskUpdated?.(refreshedTask);
      
      // Also change the work plan to reflect the change
      store.dispatch(setWorkPlanShouldUpdate(true));
    }
    return result;
  }

  useEffect(() => {
    UserService.getAllKnownUsers()
      .then((users) => {
        setUsers(users.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
      })
      .catch((e) => {
        NotificationService.error(e);
      });
  }, []);
  
  const getTechnicians = (task?: WorkTaskDTOExtended): Technician[] => {
    if(task == null) return [];
    const technicians: Technician[] = [];
    task.bookings?.forEach(b => {
      technicians.push({name: b.assignedTechnicianName, email: b.assignedTechnicianEmail});
    })
    return technicians;
  }

  const handleSelectAddress = (location?: LocationDTO) => {
    if (!location) return;

    dispatchTaskDetails({ type: 'set-task-location', payload: location });
    toggleFieldIsDirty('taskLocation', true);
  };

  const handleSelectResponsiblePerson = (user: UserDTO) => {
    dispatchTaskDetails({
      type: 'set-task',
      payload: { ...task, workTaskResponsibleName: user.name, workTaskResponsibleEmail: user.email }
    });
    toggleFieldIsDirty('workTaskResponsibleEmail', true);
    toggleFieldIsDirty('workTaskResponsibleName', true);
  };

  const handleSelectAssignedTechnician = (technician?: Technician) => {
    dispatchTaskDetails({
      type: 'set-task',
      payload: { ...task, assignedToName: technician?.name, assignedToEmail: technician?.email },
    });
    toggleFieldIsDirty('assignedToName', true);
    toggleFieldIsDirty('assignedToEmail', true);
  };


  return (
    <Section direction="row" noBorder>
      <ColumnFlex3>
        <SectionHeader>Information</SectionHeader>
        <StyledSectionContent>
          <ColumnFlex3>
            {editable ? (
              <>
                <AddressAutocomplete onSelect={handleSelectAddress} label={'Adresse'} prevLocation={task?.taskLocation} />
                <FlexRow>
                  <TextRow
                    inputMode={editable}
                    label="Etage"
                    value={taskLocation?.floor ?? ''}
                    onBlur={(text: string) => {
                      dispatchTaskDetails({ 
                        type: 'set-task-location', 
                        payload: { ...taskLocation, floor: text || undefined } 
                      });
                      toggleFieldIsDirty('taskLocation', true);
                    }}
                  />
                  <TextRow
                    inputMode={editable}
                    label="Dør"
                    value={taskLocation?.door ?? ''}
                    onBlur={(text: string) => {
                      dispatchTaskDetails({ 
                        type: 'set-task-location', 
                        payload: { ...taskLocation, door: text || undefined } 
                      });
                      toggleFieldIsDirty('taskLocation', true);
                    }}
                  />
                </FlexRow>
              </>
            ) : (
              <TextRow
                inputMode={editable}
                disabled
                label="Adresse"
                value={task?.taskLocation ? getLocationString(task?.taskLocation) : '-'}
              />
            )}
            <TextRow
              inputMode={editable}
              label="Bemærkninger til planlægning"
              value={task?.notesForPlanning ?? '-'}
              onBlur={(text: string) => dispatchTaskDetails({ type: 'set-notes-for-planning', payload: text })}
              isDirty={toggleFieldIsDirty}
              disabled={task?.status !== WorkTaskStatus.Created && task?.status !== WorkTaskStatus.Pause}
            />
            <TextRow inputMode={false} label="Note til tekniker" value={task?.notesForTechnician ?? '-'} disabled />
            <RowBase>
              <RowLabel>
                {task?.isMeterTask
                  ? getEnumDisplayValue(AssetType.InstallationNumber)
                  : task?.assetType
                  ? getEnumDisplayValue(task?.assetType)
                  : 'Asset'}
              </RowLabel>
              <RowValue>
                <FlexRow>
                  {task?.assetId ?? '-'}
                  {task?.assetId && userAllowedThroughFeatureFlag(window._env_.FeatureFlags_FieldMapsButton) && (
                    <Button
                      variant="secondary"
                      compact
                      onClick={() => openFieldMaps(task?.projectNumber)}
                    >
                      <WorldIcon size="18px" />
                      Vis i Field Maps
                    </Button>
                  )}
                </FlexRow>
              </RowValue>
            </RowBase>
            {editable ? (
              <>
                <AutoComplete
                  id="opgave-ansvarlig"
                  onChange={(event, value, reason) => value && handleSelectResponsiblePerson(value)}
                  getOptionLabel={(option) => option.name ?? ''}
                  options={users}
                  value={{ email: task?.workTaskResponsibleEmail, name: task?.workTaskResponsibleName }}
                  renderInput={(params) => <TextField {...params} label={'Opgaveansvarlig'} fullWidth required />}
                  fullWidth
                />
                <AutoComplete
                  id="tildelt-ansvarlig"
                  onChange={(event, value, reason) => value && handleSelectAssignedTechnician(value)}
                  getOptionLabel={(option) => option.name ?? ''}
                  options={getTechnicians(task)}
                  value={{ name: task?.assignedToName, email: task?.assignedToEmail }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={hasBookings ? 'Første mand' : 'Tildelt tekniker'}
                      fullWidth
                      required
                    />
                  )}
                  fullWidth
                />
              </>
            ) : (
              <>
                <StyledRowBase>
                  <RowLabel>Opgaveansvarlig</RowLabel>
                  <RowValue>
                    {task?.workTaskResponsibleName && (
                      <BasicPopover
                        buttonElement={<Person name={task.workTaskResponsibleName} />}
                        popoverElement={
                          <ContactCard
                            // This task.isMeterTask is a hack, for some reason the email is is in the name field for meter tasks.
                            email={task.isMeterTask ? task.workTaskResponsibleName : task.workTaskResponsibleEmail}
                          />
                        }
                      />
                    )}
                  </RowValue>
                </StyledRowBase>
                <StyledRowBase>
                  <RowLabel>{hasBookings ? 'Første mand' : 'Tildelt tekniker'}</RowLabel>
                  <RowValue>
                    <FlexRow>
                      {task?.assignedToName && (
                          <BasicPopover
                              buttonElement={<Person name={task.assignedToName} />}
                              popoverElement={<ContactCard email={task.assignedToEmail} />}
                          />
                      )}
                      {(hasBookings && isAssignToMeAllowed) 
                          ? <div style={{display: "flex", marginTop: '1px'}}>
                            <a onClick={ensureAssignToMe} href="#"> Overtag ansvaret</a>
                          </div> 
                          : null }
                      
                      
                    </FlexRow>  
                  </RowValue>
                </StyledRowBase>
              </>
            )}
            {task?.reasonCode && (
              <TextRow inputMode={editable} disabled label="Årsagsforklaring" value={task?.reasonCode.reasonText ?? '-'} />
            )}
            {task?.statusTransactionMessage && (
              <TextRow
                inputMode={editable}
                disabled
                label="Uddybende årsagsforklaring"
                value={task?.statusTransactionMessage}
              />
            )}
          </ColumnFlex3>
        </StyledSectionContent>
      </ColumnFlex3>
      <OuterMapContainer>
        <InnerMapContainer>
          <MapComponent
            markers={
              !!task?.taskLocation?.latitude && !!task?.taskLocation?.longitude
                ? [
                    {
                      markerType: 'Main',
                      lat: task?.taskLocation?.latitude,
                      lng: task?.taskLocation?.longitude,
                      key: 'TaskDetailsStepDetails'
                    }
                  ]
                : []
            }
            options={{
              keyboardShortcuts: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControl: false
            }}
          />
        </InnerMapContainer>
      </OuterMapContainer>
    </Section>
  );
};

const InnerMapContainer = styled.div`
  margin-left: 12px;
  height: 250px;
`;

const OuterMapContainer = styled(ColumnFlex2)`
  justify-content: center;
`;

const StyledRowBase = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
`;
